import { useTranslations } from 'next-intl';
import { type FC, useCallback, useMemo } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { Button } from '@/components/common/button/Button';
import { Flex } from '@/components/common/Flex';
import { FormSubmitButton } from '@/components/common/form/FormSubmitButton';
import { FormTextInput } from '@/components/common/form/FormTextInput';
import { Text } from '@/components/common/Text';
import { useCommonApolloOptions } from '@/hooks/use-common-apollo-options';
import { useRouter } from '@/i18n/routing';
import { getGraphqlErrors } from '@/utils/graphql';

import { gql } from '../../../../../../../../../../generated';

import styles from './EmailStep.module.scss';

type EmailStepProps = {
  onSubmit: (email: string) => unknown;
};

const RequestRestorePassword = gql(/* GraphQL */ `
  mutation RequestRestorePassword($email: String!) {
    usersRegisterRestorePassword(email: $email)
  }
`);

const useFormSchema = () => {
  const tErrors = useTranslations('errors');

  return useMemo(
    () =>
      z.object({
        email: z.string().trim().min(1, tErrors('field is required')).email(tErrors('incorrect format')).toLowerCase(),
      }),
    [tErrors]
  );
};

export type EmailStepFormData = z.TypeOf<ReturnType<typeof useFormSchema>>;

export const EmailStep: FC<EmailStepProps> = (props) => {
  const t = useTranslations('pages.account.passwordRecovery.form.steps.email');

  const { onSubmit } = props;

  const router = useRouter();
  const apolloOptions = useCommonApolloOptions();

  const [requestRestorePassword] = useMutation(RequestRestorePassword, {
    ...apolloOptions,
    context: {
      noAuth: true,
    },
  });

  const form = useForm<EmailStepFormData>({
    resolver: zodResolver(useFormSchema()),
    mode: 'onBlur',
  });

  const { register, handleSubmit } = form;

  const goBack = useCallback(() => {
    router.back();
  }, [router]);

  const request = useCallback<SubmitHandler<EmailStepFormData>>(
    async (data) => {
      const { errors } = await requestRestorePassword({
        variables: data,
      });

      if (getGraphqlErrors(errors).length) {
        return;
      }

      onSubmit(data.email);
    },
    [onSubmit, requestRestorePassword]
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(request)}>
        <Flex direction="column" gap={9}>
          <Flex className={styles.titleBlock} direction="column" gap={4}>
            <Text type="medium64" whiteSpace="preWrap">
              {t('title')}
            </Text>

            <Text type="regular18" color="white-50p">
              {t('subtitle')}
            </Text>
          </Flex>

          <Flex className={styles.formContainer} direction="column">
            <Flex direction="column" gap={1}>
              <div className={styles.descriptionBlock}>
                <Text type="medium18">{t('description')}</Text>
              </div>

              <FormTextInput
                {...register('email')}
                type="email"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="email"
                required
                placeholder={t('fields.email.placeholder')}
              />
            </Flex>

            <Flex direction="column" gap={4}>
              <FormSubmitButton>{t('buttons.sendLink')}</FormSubmitButton>

              <Button variant="secondary" onClick={goBack}>
                {t('buttons.back')}
              </Button>

              <Text className={styles.additionalInfo} type="regular14" color="dark-green2-32p">
                {t('additionalInfo')}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};
